import type { ManualGuideKey } from "./manualGuides";
import type { ManualChapterId } from "./manualChapters";

/** 절 아래 안내 글의 머리 — 참고·주의 */
export type ManualNoteKind = "note" | "caution";

/**
 * 절 — 문구는 `landing:site.manual.topics.<id>`(제목·목차용 짧은 제목·참고·단계). `guide` 는 단계별 안내 화면(`manualGuides`),
 * `extra` 는 절에만 있는 블록: 매핑 절의 GCP 안내·가이드 진입·상태표(`mapping`).
 */
export interface ManualTopic {
  id: string;
  guide?: ManualGuideKey;
  note?: ManualNoteKind;
  extra?: "mapping";
}

export interface ManualChapter {
  id: ManualChapterId;
  /** 그 장 화면을 여는 법이 있다(`landing:site.manual.chapters.<id>.open`) — 첫 절 제목 바로 아래 첫 문단으로 둔다 */
  opens?: boolean;
  topics: readonly ManualTopic[];
}

/** 여는 법 문단을 붙이는 절 — 그 장의 첫 절(service-manual 「매뉴얼 장 구성」: 첫 절 앞에 따로 떨어진 안내 단락을 두지 않는다) */
export const hasChapterIntro = (chapter: ManualChapter, topicIndex: number) =>
  Boolean(chapter.opens) && topicIndex === 0;

/** 매뉴얼 장과 절 순서(service-manual 「매뉴얼 장 구성」) — 화면 구성 안내 절은 두지 않는다 */
export const MANUAL_CHAPTERS: readonly ManualChapter[] = [
  {
    id: "c1",
    topics: [
      { id: "t-new", guide: "t-new", note: "note" },
      { id: "t-edit", guide: "t-edit", note: "note" },
      { id: "t-series", guide: "t-series", note: "note" },
      { id: "t-list", guide: "t-list", note: "note" },
      { id: "t-member", guide: "t-member" },
      { id: "t-history", guide: "t-history", note: "note" },
      { id: "t-lang", guide: "t-lang", note: "note" },
    ],
  },
  {
    id: "c2",
    topics: [
      { id: "t-upload", guide: "t-upload", note: "caution" },
      { id: "t-mapping", guide: "t-mapping", extra: "mapping" },
      { id: "t-download", guide: "t-download", note: "note" },
    ],
  },
  {
    id: "c3",
    topics: [
      { id: "t-layerdata", guide: "t-layerdata", note: "note" },
      { id: "t-terrain", guide: "t-terrain", note: "note" },
      { id: "t-ref", guide: "t-ref" },
    ],
  },
  {
    id: "c4",
    opens: true,
    topics: [
      { id: "t-2dlayer", guide: "t-2dlayer", note: "note" },
      { id: "t-folder", guide: "t-folder", note: "note" },
      { id: "t-compare", guide: "t-compare" },
      { id: "t-issue", guide: "t-issue", note: "note" },
      { id: "t-issueinfo", guide: "t-issueinfo" },
      { id: "t-measure", guide: "t-measure", note: "note" },
      { id: "t-issueexport", guide: "t-issueexport", note: "caution" },
      { id: "t-droneimg", guide: "t-droneimg" },
      { id: "t-geolog", guide: "t-geolog" },
      { id: "t-report", guide: "t-report", note: "note" },
      { id: "t-capture", guide: "t-capture" },
    ],
  },
  {
    id: "c5",
    opens: true,
    topics: [
      { id: "t-camera", guide: "t-camera", note: "note" },
      { id: "t-fly", guide: "t-fly" },
      { id: "t-3dlayer", guide: "t-3dlayer", note: "note" },
      { id: "t-measure3d", guide: "t-measure3d", note: "caution" },
      { id: "t-area3d", guide: "t-area3d", note: "note" },
      { id: "t-issue3d", guide: "t-issue3d", note: "note" },
      { id: "t-clip", guide: "t-clip", note: "note" },
      { id: "t-clipedit", guide: "t-clipedit", note: "note" },
      { id: "t-vector", guide: "t-vector", note: "note" },
    ],
  },
  {
    id: "c6",
    topics: [
      { id: "t-pf-dxf", guide: "t-pf-dxf", note: "note" },
      { id: "t-pf-dxf3d", guide: "t-pf-dxf3d" },
      { id: "t-pf-export", guide: "t-pf-export", note: "note" },
      { id: "t-pf-issue", guide: "t-pf-issue" },
      { id: "t-pf-issue3d", guide: "t-pf-issue3d" },
      { id: "t-pf-3d", guide: "t-pf-3d" },
      { id: "t-pf-view", guide: "t-pf-view" },
      { id: "t-pf-layers", guide: "t-pf-layers", note: "note" },
      { id: "t-pf-measure", guide: "t-pf-measure" },
      { id: "t-pf-image", guide: "t-pf-image" },
      { id: "t-pf-qty", guide: "t-pf-qty" },
    ],
  },
  {
    id: "c7",
    opens: true,
    topics: [
      { id: "t-members", guide: "t-members" },
      { id: "t-equip", guide: "t-equip", note: "note" },
      { id: "t-route", guide: "t-route", note: "note" },
    ],
  },
  {
    id: "c8",
    opens: true,
    topics: [
      { id: "t-ewzone", guide: "t-ewzone", note: "note" },
      { id: "t-ewtable", guide: "t-ewtable", note: "note" },
      { id: "t-linear", guide: "t-linear", note: "note" },
    ],
  },
];
